import React, { useCallback } from "react";
import styled from "@emotion/styled";
import LuClock8 from "@assets/icons/luclock8.svg";
import { LearnItem, MediaType } from "@domain/learn/learn-service";
import { GifPlayer } from "@ui/components/common/gif-player";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { formatTitle } from "@utils/string-utils";
import { useMediaDuration } from "@ui/hooks/use-media-duration";
import { useI18n } from "@core/i18n/i18n";
import dayjs from "dayjs";

interface MediaThumbnailProps {
	item: LearnItem;
}

const MediaThumbnail: React.FC<MediaThumbnailProps> = ({ item }) => {
	if (item.mediaType === MediaType.ARTICLE && item.mediaUrl) {
		return <img src={item.mediaUrl} alt={`thumbnail-${item.title}`} loading="lazy" />;
	}
	if (item.mediaType === MediaType.VIDEO && item.previewUrl) {
		return <GifPlayer url={item.previewUrl} alt={`thumbnail-${item.title}`} repeatDuration={1600} />;
	}
	return (
		<Placeholder>
			<MediaLabel>{item.mediaType}</MediaLabel>
		</Placeholder>
	);
};

interface LearnItemCardProps {
	item: LearnItem;
	onNavigate: (documentId: string) => void;
}

export const LearnItemCard: React.FC<LearnItemCardProps> = ({ item, onNavigate }) => {
	const { formatDateFull } = useI18n();
	const handleClick = useCallback(() => {
		onNavigate(item.documentId);
	}, [item.documentId, onNavigate]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onNavigate(item.documentId);
			}
		},
		[item.documentId, onNavigate],
	);
	const { durationLabel } = useMediaDuration(item.mediaType, item.mediaUrl, item.duration);

	return (
		<ItemCard
			role="button"
			tabIndex={0}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			aria-label={`View ${item.title}`}
		>
			<MediaBadge aria-label={`Content type: ${item.mediaType}`}>{item.mediaType}</MediaBadge>
			{durationLabel && (
				<DurationRow aria-label={`Duration: ${durationLabel}`}>
					<LuClock8 width={13.33} height={13.33} aria-hidden="true" />
					<DurationText>{durationLabel}</DurationText>
				</DurationRow>
			)}

			<ThumbWrapper>
				<MediaThumbnail item={item} />
			</ThumbWrapper>

			<CardContent>
				<CardTitle>{formatTitle(item.title)}</CardTitle>
				<CardMeta>
					{item.author && <AuthorName>{item.author}</AuthorName>}
					{item.publicationDate && <PublicationDate> {formatDateFull(dayjs(item.publicationDate))}</PublicationDate>}
				</CardMeta>
			</CardContent>
		</ItemCard>
	);
};

const ItemCard = styled.article`
	width: 270px;
	height: 317px;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	border-radius: 8px;
	padding: 16px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
	background: ${colors.primary10};
	box-sizing: border-box;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	&:focus {
		outline: 2px solid ${colors.primary};
		outline-offset: 2px;
	}

	&:active {
		transform: translateY(0);
	}
`;

const ThumbWrapper = styled.div`
	width: 100%;
	height: 150px;
	flex-shrink: 0;
	overflow: hidden;
	border-radius: 6px;
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`;

const Placeholder = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${colors.grey10};
`;

const MediaLabel = styled.span`
	font-family: ${fonts.body};
	font-size: 12px;
	color: ${colors.grey50};
	font-weight: 700;
`;

const MediaBadge = styled.div`
	font-family: ${fonts.header};
	font-size: 16px;
	font-weight: 700;
	font-style: normal;
	color: ${colors.primaryDark};
	border-radius: 16px;
	display: inline-block;
	align-self: flex-start;
`;

const DurationRow = styled.div`
	display: flex;
	align-items: center;
	gap: 3px;
	margin-bottom: 10px;
	color: ${colors.grey};
	font-size: 13px;
	font-weight: 600;
	svg {
		fill: ${colors.grey};
		color: ${colors.grey};
	}
`;

const DurationText = styled.span`
	font-family: ${fonts.body};
	color: ${colors.grey};
	font-size: 12px;
`;

const CardContent = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	text-align: left;
`;

const CardTitle = styled.h3`
	margin: 10px 0 6px 0;
	font-size: 18px;
	font-weight: 700;
	font-family: ${fonts.header};
	color: ${colors.black};
	line-height: 1.2;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	&:hover,
	&:focus {
		text-decoration: underline;
		cursor: pointer;
	}
`;

const CardMeta = styled.div`
	font-size: 12px;
	color: ${colors.grey50};
`;

const AuthorName = styled.span`
	font-family: ${fonts.body};
	font-weight: 700;
	color: ${colors.grey};
	font-size: 12px;
`;

const PublicationDate = styled.span`
	font-family: ${fonts.body};
	font-size: 12px;
	font-weight: 600;
	color: ${colors.grey};
	margin-left: 6px;
`;
